import React, { useContext, useState } from 'react';
import { Colors, SkeletonView, Text, TextField, View } from 'react-native-ui-lib';
import { LocalizedStringsContext } from '../localization/localization.native';
import { addComment, useComments } from '../firebaseWrapper/firebaseWrapper';
import { canAddComment } from '../helperFunctions';
import { ElementPath } from '../typings/types';
import { styles } from '../styles';
import { ButtonPrimary } from './Buttons';
import { Timestamp, UserName } from './helperComponents.native';

type CommentSectionProps = {
  // Path of the Statement or Argument being commented on.
  info: ElementPath;
  // The commented-on container's own author (its `userId`). See canAddComment in
  // helperFunctions.ts for why this -- and not just group membership -- gates posting.
  authorId: string;
  groupId: string;
  memberId?: string;
  groupAdminLoggedIn: boolean;
  navigation?: any;
};

/**
 * Comment thread for a Statement or Argument inside a group. Within a group, only the group
 * admin and the container's own author may add a comment (see canAddComment); everyone who can
 * already see the container can read its comments.
 */
export function CommentSection({
  info,
  authorId,
  groupId,
  memberId,
  groupAdminLoggedIn,
  navigation,
}: CommentSectionProps): JSX.Element {
  const strings = useContext(LocalizedStringsContext);
  const { comments, loading, error } = useComments(info);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canComment = canAddComment(authorId, groupAdminLoggedIn, memberId);

  const submitComment = () => {
    const content = newComment.trim();
    if (!content || submitting) {
      return;
    }
    setSubmitting(true);
    addComment(info, content, memberId)
      .then(() => {
        setNewComment('');
      })
      .catch(error => {
        console.error('Error adding comment: ', error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <View marginT-10 marginH-10>
      <Text h4 textPrimary marginB-5>
        {strings.comments}
      </Text>
      {loading ? (
        <SkeletonView
          width={200}
          height={16}
          borderRadius={2}
          colors={[Colors.skeletonPrimary, Colors.skeletonSecondary, Colors.skeletonPrimary]}
        />
      ) : error ? null : comments.length === 0 ? (
        <Text small textSecondary>
          {strings.no_comments_info}
        </Text>
      ) : (
        comments.map(comment => (
          <View key={comment.id} row centerV marginV-5 style={{ flexWrap: 'wrap' }}>
            <UserName userId={comment.userId} navigation={navigation} inGroup groupId={groupId} />
            <Text small textPrimary marginL-5 style={{ flexShrink: 1 }}>
              {comment.content}
            </Text>
            <Timestamp timestamp={comment.creationTime?.toDate()} />
          </View>
        ))
      )}
      {canComment ? (
        <View row centerV marginT-5>
          <TextField
            testID="new-comment-input"
            style={[styles.inputContainer, { flex: 1 }]}
            placeholder={strings.add_comment_placeholder}
            placeholderTextColor={Colors.textSecondary}
            value={newComment}
            onChangeText={setNewComment}
          />
          <ButtonPrimary testID="submit-comment" label={strings.add_comment} onPress={submitComment} />
        </View>
      ) : null}
    </View>
  );
}
